import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges, Output, EventEmitter, ViewChild
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgApexchartsModule } from "ng-apexcharts";
import { CoreService } from "../../../services/core.service";
import { ChartComponent } from "ng-apexcharts";
import ApexCharts from 'apexcharts';


@Component({
  selector: "app-followers-chart",
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  templateUrl: "./followers-chart.component.html",
  styleUrl: "./followers-chart.component.scss",
})
export class FollowersChartComponent implements OnInit, OnChanges {
  @Input() selectedSiteId: number | null = null;
  @Input() selectedPlatformId: number | null = null;
  @Input() selectedPeriod: string | null = null;
  @Input() pages: any[] = [];
  @Input() title: string = "";
  @Output() dataReady = new EventEmitter<any[]>();
  @ViewChild("chartRef") chart!: ChartComponent;

  metricValue = 0;
  metricDiff = 0;
  chartOptions: any = {};
  companyID: number | null = null;

  isLoaded = false;
  private lastPayloadKey: string | null = null;

  constructor(private coreService: CoreService) { }

  ngOnInit(): void {
    this.tryLoadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.tryLoadData();
  }

  private tryLoadData(): void {
    if (
      !this.selectedSiteId ||
      !this.selectedPlatformId ||
      !this.selectedPeriod ||
      !this.pages?.length
    ) {
      return;
    }

    const platformName =
      (this.pages?.[0]?.platform || "").toLowerCase();

    const isInstagram =
      platformName.includes("instagram") ||
      platformName.includes("insta") ||
      platformName.includes("ig");

    const periodToUse = isInstagram ? "day" : this.selectedPeriod;

    const payloadKey = JSON.stringify({
      site: this.selectedSiteId,
      platform: this.selectedPlatformId,
      period: periodToUse,
    });

    if (payloadKey === this.lastPayloadKey) return;
    this.lastPayloadKey = payloadKey;

    this.loadFollowersData(isInstagram, periodToUse);
  }

  private loadFollowersData(
    isInstagram: boolean,
    periodToUse: string
  ): void {
    this.companyID = this.coreService.user?.companyID || 0;
    this.isLoaded = false;

    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: periodToUse,
      pages: this.pages,
    };

    const endpoint = isInstagram
      ? `querymetrics/parseInstaAnalytics/${this.companyID}?metric=follower_count`
      : `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_follows`;

    this.coreService.postData(endpoint, payload).subscribe({
      next: (res) => {
        /* ==============================
         * INSTAGRAM
         * ============================== */
        if (isInstagram) {
          const meta = res.meta || res.original?.meta || {};
          const valuesRaw: number[] = meta.adjustedValues || [];
          const datesRaw: string[] = meta.dates || [];

          if (!valuesRaw.length || !datesRaw.length) {
            this.metricValue = 0;
            this.metricDiff = 0;
            return;
          }

          let values = valuesRaw.map((val, i) => ({
            value: val,
            date: new Date(datesRaw[i].replace("+0000", "Z")),
          }));

          // IG offset fix
          values.forEach(v =>
            v.date.setDate(v.date.getDate() + 1)
          );

          if (this.selectedPeriod === "week" && values.length > 7) {
            values = values.slice(-7);
          }

          this.metricValue = values.at(-1)?.value || 0;
          this.metricDiff =
            this.metricValue - (values[0]?.value || 0);

          const categories = values.map(v =>
            v.date.toLocaleDateString("el-GR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          );

          const formattedData = values.map(v => ({
            date: v.date,
            value: v.value
          }));

          this.buildChart(values.map(v => v.value), categories);
          this.dataReady.emit(formattedData);
          return;
        }

        /* ==============================
         * FACEBOOK
         * ============================== */
        const data =
          res.data?.[0]?.insight?.data?.[0]?.values || [];

        if (!data.length) {
          this.metricValue = 0;
          this.metricDiff = 0;
          return;
        }

        let values = data.map((v: any) => ({
          value: v.value,
          date: new Date(v.end_time),
        }));

        if (this.selectedPeriod === "week" && values.length > 7) {
          values = values.slice(-7);
        }

        this.metricValue = values.at(-1)?.value || 0;
        this.metricDiff =
          this.metricValue - (values[0]?.value || 0);

        const categories = values.map((v: any) =>
          v.date.toLocaleDateString("el-GR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        );

        const formattedData = values.map((v: any) => ({
          date: v.date,
          value: v.value
        }));

        this.buildChart(values.map((v: any) => v.value), categories);
        this.dataReady.emit(formattedData);
      },
      complete: () => {
        this.isLoaded = true;
      },
    });
  }

  private buildChart(data: number[], categories: string[]) {
    this.chartOptions = {
      series: [{ name: "Followers", data }],
      chart: {
        id: 'followersChart',
        type: "area",
        height: 360,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { curve: "smooth", width: 2, colors: ["#6ab7ff"] },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#f1f3f4",
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      yaxis: {
        tickAmount: 6,
        labels: {
          style: { colors: "#777", fontSize: "12px" },
          formatter: (val: number, index: number) =>
            index === 0 ? "" : val.toLocaleString(),
        },
      },
      xaxis: {
        categories,
        tickAmount: 6,
        labels: { style: { colors: "#777", fontSize: "12px" } },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (val: number) =>
            val.toLocaleString("el-GR"),
        },
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
      },
      title: {
        align: "left",
        style: {
          fontSize: "15px",
          fontWeight: "600",
          color: "#333",
        },
      },
    };
  }

exportChartImage(): Promise<string> {

  return ApexCharts.exec(
    this.chartOptions.chart.id,
    'dataURI'
  ).then((res: any) => {
    return res.imgURI; // base64
  });
}

}
